import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Toast } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';

const CompleteRecipeCookingButton = ({ recipeId, appUserId, servingSize }) => {
    const { sessionToken } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [actualServings, setActualServings] = useState(servingSize);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [errorCompletingCooking, setErrorCompletingCooking] = useState(false);
    const [errorCompletingCookingMessage, setErrorCompletingCookingMessage] = useState('');

    // Update actualServings when servingSize prop changes
    useEffect(() => {
        setActualServings(servingSize);
    }, [servingSize]);

    const showRecipeCompletedToast = (message) => {
        setToastMessage(message);
        setShowToast(true);
        
    }

    const handleCompleteCooking = async () => {
        try {

            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            const response = await fetchWithCSRF('/api/v1/recipe/complete_recipe_cooking', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    recipe_id: recipeId,
                    app_user_id: appUserId,
                    serving_size: actualServings
                })
            });

            if (!response.ok) {
                throw new Error('Failed to complete cooking for this recipe');
            }

            const data = await response.json();
            console.log('Recipe cooking completed:', data);
            setShowModal(false);
            setErrorCompletingCooking(false);
            setErrorCompletingCookingMessage('');
            showRecipeCompletedToast('Ingredients removed from pantry!');
        } catch (error) {
            console.error('Error completing recipe cooking:', error);
            setErrorCompletingCooking(true);
            setErrorCompletingCookingMessage('Failed to mark recipe as cooked. Please refresh the page and try again.');
        }
    };

    const handleModalClick = (e) => {
        e.stopPropagation();
    };

    const handleDecreaseServings = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setActualServings(prev => Math.max(1, prev - 1));
    };

    const handleIncreaseServings = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setActualServings(prev => Math.min(16, prev + 1));
    };

    return (
        <>
            <button 
                onClick={(e) => {
                    e.stopPropagation();
                    setShowModal(true);
                }}
                style={{ 
                    backgroundColor: '#52cd92',
                    borderColor: 'transparent',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    marginBottom: '2rem',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    transition: 'transform 0.2s, background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#45b07c'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#52cd92'}
            >
                I Cooked This
            </button>

            <Modal 
                show={showModal} 
                onHide={() => setShowModal(false)}
                backdrop="static"
                keyboard={false}
                onClick={handleModalClick}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Complete Cooking</Modal.Title>
                </Modal.Header>
                <Modal.Body onClick={handleModalClick}>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-center w-100">How many servings did you cook?</Form.Label>
                            <div className="d-flex align-items-center justify-content-center mt-4 gap-3" onClick={handleModalClick}>
                                <button 
                                    type="button"
                                    className="btn p-0" 
                                    onClick={handleDecreaseServings}
                                    style={{ 
                                        border: 'none', 
                                        background: 'none',
                                        color: '#52cd92',
                                        fontSize: '1.5rem',
                                        lineHeight: 1,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    −
                                </button>
                                <div className="d-flex flex-column align-items-center">
                                    <span className="h4 mb-0">{actualServings}</span>
                                    <small className="text-muted">{actualServings === 1 ? 'serving' : 'servings'}</small>
                                </div>
                                <button 
                                    type="button"
                                    className="btn p-0" 
                                    onClick={handleIncreaseServings}
                                    style={{ 
                                        border: 'none', 
                                        background: 'none',
                                        color: '#52cd92',
                                        fontSize: '1.5rem',
                                        lineHeight: 1,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    +
                                </button>
                            </div>
                            <small className="text-muted mt-5 mb-1 d-block text-center">
                                Completing this recipe will remove the ingredients used in this recipe from your pantry
                            </small>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer onClick={handleModalClick}>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCompleteCooking} className="add-to-meal-plan-btn">
                        Complete Cooking
                    </Button>
                </Modal.Footer>
            </Modal>

            {/*Error Completing Cooking Toast Notification */}
            <Toast 
                show={errorCompletingCooking} 
                onClose={() => setErrorCompletingCooking(false)}
                style={{
                    position: 'fixed',
                    top: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    backgroundColor: '#dc3545',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    width: '50%'
                }}
            >
                <Toast.Header style={{ 
                    backgroundColor: '#dc3545', 
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                    width: '100%'
                }}>
                    <strong style={{ color: 'white' }}>{errorCompletingCookingMessage}</strong>
                </Toast.Header>
            </Toast>
            

            {/* Toast Notification */}
            <Toast 
                show={showToast} 
                onClose={() => setShowToast(false)}
                style={{
                    position: 'fixed',
                    top: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    backgroundColor: '#52cd92',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    width: '50%'
                }}
               
            >
                <Toast.Header style={{ 
                    backgroundColor: '#52cd92', 
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                    width: '100%'
                }}>
                    <strong style={{ color: 'white' }}>{toastMessage}</strong>
                </Toast.Header>
            </Toast>
        </>
    );
};

export default CompleteRecipeCookingButton;